/**
 * The conversion engine — stage composition.
 *
 * Source: (`convertLatexToHtml`). Spec:
 * `docs/spec/00-MASTER-SPEC.md` §1 (the ordering contract) and §3 (R-001 … R-350).
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────
 * THE ORDER IN THIS FILE IS THE CONTRACT.
 *
 * §1 lists 25 stages, S0 … S24. Almost every confirmed defect in `docs/DEFERRED-FIXES.md` is
 * an order-of-operations consequence, so moving a call here changes published scripture:
 *
 *   S7  before S11  ⇒ Mode A kills every hand-written Bahá'í handler.
 *   S8  before S11  ⇒ note bodies are frozen and get only the 9-rule set at.
 *   S10 before S11  ⇒ `\$` is eaten by inline math before R-206 can protect it (DF-5).
 *   S11 before S12  ⇒ `--`→`–` makes `[label=--]` unreachable; `/\\ /g`→`' '` merges table
 *                     rows (DF-3); `\&`→`&amp;` shreds a table row (DF-4).
 *   S13 after  S12  ⇒ `\\` survives into table row splitting.
 *   S16 before S17  ⇒ empty `<p>`s created later by S17 are never cleaned up.
 *
 * REPRODUCE BUG-FOR-BUG (CLAUDE.md §3). Nothing here "fixes" anything.
 * ─────────────────────────────────────────────────────────────────────────────────────────
 *
 * DELIBERATE, NON-OBSERVABLE DEVIATIONS FROM THE SOURCE (allowed by CLAUDE.md § Code
 * standards; each one is invisible in the returned `{ html, css }`):
 *
 *   1. Module-global state → `ConversionContext` (G-29). The source writes `documentMetadata`
 * and `globalFootnotesHtml` at module scope, which makes the
 *      engine non-reentrant. Here they are fields of a per-call context object.
 *   2. The three `console.log` diagnostics ( `nverset` dump `CSS text-align`)
 *      are omitted. The converter must be pure — no I/O — and the logs do not touch the
 *      returned value.
 *   3. `import 'katex/dist/katex.min.css'` is not ported: a Vite side-effect import
 *      with no effect on output that would break in a Worker.
 *
 * Duplicate implementations resolved here, once, so the engine has exactly one owner per rule
 * (see each stage comment for the citation):
 *   R-267/R-268 `\marginpar`      → `notes.ts`         (not commands-text.ts's copy)
 *   R-269       `\afficherEndNotes` → `notes.ts`       (not commands-domain.ts's copy)
 *   R-311       `generateHeadingId` → `toc.ts`         (not commands-text.ts's copy)
 *   R-292/R-293 equation / align  → `environments.ts`  (not math.ts's copies)
 * The unused copies stay in their modules as tested references; this file simply does not
 * import them. Nothing is re-exported with `export *` — several names collide.
 */

import { applyDomainCommands } from './commands-domain'
import {
  extractCommandContent,
  processAddContentsLine,
  processAlignment,
  processDashesAndSymbols,
  processFontSizes,
  processFrenchTypography,
  processHorizontalSpacing,
  processIndentation,
  processLabelsAndRefs,
  processLineAndPageBreaks,
  processMaketitle,
  processNaiveTextStyling,
  processPageStyleCommands,
  processPreambleOnlyCommands,
  processSections,
  processSpecialCharacters,
  processTextFormatting,
  processVerticalSpacing,
  removePreambleCommands,
} from './commands-text'
import { createContext, type ConversionContext } from './context'
import { processEnvironments } from './environments'
import { expandMacros, extractMacros, filterManualMacros } from './macros'
import { processDisplayMath, processInlineMath, renderMath } from './math'
import { injectNotes, processFootnotesEarly, processMarginpar, removeReverseMarginpar } from './notes'
import {
  cleanupEmptyParagraphs,
  collapseBlankLinesAndTrim,
  convertLineBreaks,
  dedupeConsecutivePageBreaks,
  processParagraphs,
  stripEmptyBraces,
  suppressPageBreakBeforeHiddenToc,
} from './paragraphs'
import { generateCSS } from './css'
import { extractDocumentSettings } from './settings'
import { buildTableOfContents, insertTableOfContents } from './toc'
import type { ConversionResult } from './types'

/**
 * S1 — `stripComments` (R-002 … R-006).
 *
 * Two passes, both `m`-flagged:
 *   1. `/^%.*$/gm` — a full-line comment becomes an EMPTY LINE, it is not deleted. Line count
 *      is preserved, which matters because several later rules split on blank lines.
 *   2. `/(?<!\\)%.*$/gm` — an inline comment, unless the `%` is escaped. The negative
 *      lookbehind is mandatory and requires an ES2018+ regex engine.
 *
 * DF-6 / G-11 / R-005 STARTS HERE, and is completed at S11 R-205. The lookbehind sees the
 * backslash in `\\%` and declines to strip, so the comment text survives; R-205 (`\%` → `%`)
 * then eats the SECOND backslash. `Test \\% pas commentaire` publishes as
 * `Test \% pas commentaire` — a comment rendered as scripture. Keep it.
 *
 * Local to this file because S1 is a pipeline step with no other consumer; every stage module
 * takes comment-stripped text as a precondition.
 */
export function stripComments(text: string): string {
  // R-002
  let result = text.replace(/^%.*$/gm, '')
  // R-003 / R-004 / R-005
  result = result.replace(/(?<!\\)%.*$/gm, '')
  return result
}

/**
 * Stage S11 — `processCommands`, reassembled from its three owning modules.
 *
 * 21 slots in exact source order; the slot numbering is `commands-text.ts:S11_ORDER`. Slot 4
 * is the Bahá'í domain block and slot 19 the notes-injection
 * block; every other slot is generic.
 *
 * `now` is threaded so `\today` renders from ONE instant at both of its call sites
 * (en-US, inside `\maketitle`) and (fr-FR, body text). G-25: two locales in one French
 * document, both reproduced.
 */
function processCommands(html: string, ctx: ConversionContext, now: Date): string {
  let out = html

  out = removePreambleCommands(out) //                     1  R-142…R-149
  out = processMaketitle(out, ctx.metadata, now) //        2  R-150
  out = processTextFormatting(out) //                      3  R-151…R-157
  out = applyDomainCommands(out, ctx) //                   4  R-158…R-175
  out = processAddContentsLine(out) //                     5  R-176
  out = processPageStyleCommands(out) //                   6  R-177…R-181
  out = processSections(out) //                            7  R-182…R-185
  out = processNaiveTextStyling(out) //                    8  R-186…R-190
  out = processFontSizes(out) //                           9  R-191…R-203
  out = processSpecialCharacters(out) //                  10  R-204…R-211
  out = processFrenchTypography(out) //                   11  R-212…R-219
  out = processDashesAndSymbols(out, now) //              12  R-220…R-233
  out = processLineAndPageBreaks(out) //                  13  R-234…R-241
  out = processVerticalSpacing(out) //                    14  R-242…R-246
  out = processHorizontalSpacing(out) //                  15  R-247…R-257
  out = processAlignment(out) //                          16  R-258…R-263
  out = processIndentation(out) //                        17  R-264…R-266
  out = processMarginpar(out) //                          18  R-267
  out = removeReverseMarginpar(out) //                    18  R-268
  out = injectNotes(out, ctx.endnotesHtml) //             19  R-269…R-271
  out = processLabelsAndRefs(out) //                      20  R-272…R-274
  out = processPreambleOnlyCommands(out) //               21  R-275…R-279

  return out
}

/**
 * Convert a LaTeX document to HTML + CSS.
 *
 * @param latex the assembled input — in production `preamble + "\n\n" + docContent`
 *   (S0, `ConverterApp.tsx:112`). An empty preamble box gives Mode B.
 * @returns `{ html, css }`, byte-identical to the original converter's output.
 */
export function convertLatexToHtml(latex: string): ConversionResult {
  // The clock is read ONCE per conversion and threaded to both `\today` sites. The source
  // calls `new Date()` twice; on any realistic input the two land in the same
  // millisecond, and a single read is what makes the conversion deterministic under test.
  const now = new Date()

  // ── S1 — strip comments ───────────────────────────────────────────────────────
  const cleanLatex = stripComments(latex)

  // ── S2 — harvest macros from the WHOLE document, preamble included ────────────
  // NOTHING IS EVER REMOVED FROM THE PREAMBLE BEFORE THIS POINT — owner ruling,
  // 2026-08-30. A stage that deleted text used to sit here (`resolveIfPandoc`,
  // added 2026-08-29 and removed the next day). It cut the Pandoc half out of every
  // `\ifpandoc` block, and the damage was exactly the damage any such stage causes:
  // the only `\renewcommand{\thefootnote}` in the preamble lived in the half it
  // deleted, so footnote numbering silently vanished and fell back to symbols —
  // published as `‡‡` in L'Appel du Seigneur des armées. Nothing downstream could
  // tell an instruction had been taken away rather than never written.
  //
  // XeLaTeX does not work that way either: it *skips executing* a false branch, it
  // never rewrites the source. `\ifpandoc` is Pandoc's switch and has no business in
  // an HTML target, so it was removed from `preambuleV3.tex` itself instead — see
  // the `% CONVERTISSEUR:` note on the notes block there.
  //
  // Duplicates that genuinely remain are still preserved here, and for those the
  // FIRST definition wins (R-017 / G-15).
  const rawMacros = extractMacros(cleanLatex)

  // ── S2b — the `manualMacros` filter ─────────────────────────────────────
  // The Set is EMPTY, so nothing is ever filtered. This single fact is what makes Mode A the
  // production reality (R-011).
  const macros = filterManualMacros(rawMacros)

  // ── S3 — document settings, also over the whole document ─────────────────────
  const settings = extractDocumentSettings(cleanLatex)

  // Per-conversion state, replacing the source's module globals (G-29). Created after S3 so
  // the context carries the real settings rather than `context.ts`'s placeholder defaults.
  const ctx: ConversionContext = createContext()
  ctx.macros = macros
  ctx.settings = settings

  // ── S4 — metadata capture ───────────────────────────────────────────────
  // From the CLEANED FULL document, before the body is sliced out — which is precisely why
  // `\title`/`\author`/`\date` written in the preamble reach `\maketitle` at S11.
  ctx.metadata = {
    title: extractCommandContent(cleanLatex, 'title'),
    author: extractCommandContent(cleanLatex, 'author'),
    date: extractCommandContent(cleanLatex, 'date'),
  }

  // ── S5 — body slice ─────────────────────────────────────────────────────
  // R-077: if `\begin{document}` is absent the WHOLE document — preamble included — becomes
  // the body, and every preamble construct is published as text. `\end{document}` missing
  // just runs to the end of the string. Non-global matches: the FIRST of each wins, and no
  // check is made that the end follows the beginning.
  let documentBody = cleanLatex
  const beginDocMatch = cleanLatex.match(/\\begin\{document\}/)
  const endDocMatch = cleanLatex.match(/\\end\{document\}/)
  if (beginDocMatch && beginDocMatch.index !== undefined) {
    const startIndex = beginDocMatch.index + beginDocMatch[0].length
    const endIndex = endDocMatch?.index ?? cleanLatex.length
    documentBody = cleanLatex.substring(startIndex, endIndex)
  }

  // ── S6 — reset the notes accumulator ─────────────────────────────────────────
  ctx.endnotesHtml = ''

  // ── S7 — expand macros, BODY ONLY ────────────────────────────────────────────
  // 10 passes max (R-016). This is the stage that decides Mode A vs Mode B.
  let html = expandMacros(documentBody, macros)

  // ── S8 — the early note pass ────────────────────────────────────────────
  // Runs AFTER expansion, so it sees the `\footnote`/`\endnote` that `\note` expanded into.
  // Note bodies are frozen here and never see S9-S12 (pipeline consequence 2).
  const footnoteResult = processFootnotesEarly(html, settings)
  html = footnoteResult.text
  ctx.endnotesHtml = footnoteResult.footnotesHtml

  // ── S9 — display math ────────────────────────────────────────────────────────
  html = processDisplayMath(html)

  // ── S10 — inline math ────────────────────────────────────────────────────────
  // DF-5 LIVES HERE: `\$` is consumed as a math delimiter, so the text between two escaped
  // dollars is replaced by a KaTeX error span — and the spans straddle paragraph boundaries.
  html = processInlineMath(html)

  // ── S11 — processCommands, 138 ordered rules ─────────────────────────────────
  html = processCommands(html, ctx, now)

  // ── S12 — processEnvironments, 18 ordered rules ──────────────────────────────
  // `processParagraphs` is injected as the source's `processInner` and `renderMath`
  // as the equation/align renderer. Those two rules live INSIDE this stage
  // — running math.ts's standalone copies earlier would drop the `<p>` wrapper around a
  // display equation inside a quote.
  html = processEnvironments(html, { processParagraphs, renderMath })

  // ── S13 — `\\` → `<br>` ──────────────────────────────────────────────────────
  // Deferred until after S12 so tables could split rows on `\\`.
  html = convertLineBreaks(html)

  // ── S14 — strip `{}` ─────────────────────────────────────────────────────────
  html = stripEmptyBraces(html)

  // ── S15 — paragraph wrapping ─────────────────────────────────────────────────
  html = processParagraphs(html)

  // ── S16 — empty-paragraph cleanup, 3 regexes ────────────────────────────
  // MUST precede S17: paragraphs left empty by S17's marker-span deletion are never cleaned.
  html = cleanupEmptyParagraphs(html)

  // ── S17 — build the TOC and inject heading anchors ──────────────────────
  // `??` and not `||`, since 2026-09-07: 0 is a depth a document may legitimately ask for, and
  // `||` treated it as "absent" and substituted 4 — so `\setcounter{tocdepth}{0}` published a
  // four-level table of contents instead of an empty one. The fallback still covers the only
  // case it was ever for, an absent counter.
  const tocDepth = settings.tocDepth ?? 4
  const { html: htmlWithIds, toc: generatedToc } = buildTableOfContents(html, tocDepth)
  html = htmlWithIds

  // ── S18 — substitute the TOC into every placeholder ──────────────────────────
  html = insertTableOfContents(html, generatedToc)

  // ── S19 — hide a page break that immediately precedes a hidden TOC ──────
  html = suppressPageBreakBeforeHiddenToc(html)

  // ( is a `console.log`; omitted — see the deviations note at the top.)

  // ── S20 — strip `{}` again ───────────────────────────────────────────────────
  html = stripEmptyBraces(html)

  // ── S21 — collapse consecutive page breaks ──────────────────────────────
  html = dedupeConsecutivePageBreaks(html)

  // ── S22 — collapse blank runs, then trim ────────────────────────────────
  html = collapseBlankLinesAndTrim(html)

  // ── S23 — the stylesheet ─────────────────────────────────────────────────────
  // A pure function of the settings; it never sees the HTML.
  const css = generateCSS(settings)

  // ( is a `console.log`; omitted.)

  // ── S24 — return ─────────────────────────────────────────────────────────────
  return { html, css }
}

/* ───────────────────────────────── public surface ─────────────────────────────────────── */

export { generateFullHtml, generatePreviewHtml } from './templates'
export type { ConversionResult, DocumentMetadata, DocumentSettings, FontSettings } from './types'
